/**
 * Acciones NgRx para la gestión del estado de recetas médicas.
 *
 * @description
 * Define todas las acciones disponibles para el manejo del estado de recetas médicas
 * en el store de NgRx, incluyendo operaciones de creación, lectura, actualización y
 * eliminación (CRUD), así como la gestión de recetas por profesional y por paciente.
 *
 * @module ClinicalRecipeActions
 */

import { HttpErrorResponse } from '@angular/common/http';

import { ParamsToFindByPatient } from '@components/patient/interfaces/patient.interface';
import { createAction, props } from '@ngrx/store';
import { Paperwork } from '@shared/interfaces/paperwork.interface';
import { Recipe } from '@shared/interfaces/recipe.interface';
import { MagistralRecipe, MagistralRecipeV2 } from '@shared/interfaces/recipe-magistral.interface';
import { RecipeV2 } from '@shared/interfaces/recipe-v2.interface';
import { ReportRecipeResponse } from '@shared/interfaces/report.interface';

/** Acción para guardar una receta médica en el backend */
export const PUT_RECIPE     = createAction(
    '[medicamentos] PUT Receta Médica',
  props<{ valuesToSave: Paperwork<Recipe | MagistralRecipe> }>(),
);

/** Acción de éxito al guardar una receta médica */
export const PUT_RECIPE_OK  = createAction(
    '[medicamentos] OK PUT Receta Médica',
  props<{ recipe: Paperwork<Recipe | MagistralRecipe> }>(),
);

/** Acción de error al guardar una receta médica */
export const PUT_RECIPE_ERR = createAction(
    '[medicamentos] ERR PUT Receta Médica',
    props<{ payload: HttpErrorResponse }>(),
);

/** Acción para cargar recetas del profesional (versión 1) */
export const LOAD_RECIPES_BY_PROFESSIONAL     = createAction(
    '[medicamentos] Cargar medicamentos por paciente',
    props<{ gsiMultiCondition1St: string }>(),
);

/** Acción de éxito al cargar recetas del profesional (versión 1) */
export const LOAD_RECIPES_BY_PROFESSIONAL_OK  = createAction(
    '[medicamentos] OK medicamentos por paciente',
  props<{ recipes: Paperwork<Recipe | RecipeV2 | MagistralRecipe | MagistralRecipeV2>[] }>(),
);

/** Acción de error al cargar recetas del profesional (versión 1) */
export const LOAD_RECIPES_BY_PROFESSIONAL_ERR = createAction(
    '[medicamentos] ERR medicamentos por paciente',
    props<{ payload: HttpErrorResponse }>(),
);

/** Acción para cargar recetas del profesional con formato de reporte (versión 2) */
export const LOAD_RECIPES_BY_PROFESSIONAL_V2     = createAction(
    '[medicamentos] Cargar medicamentos por paciente',
    props<{ gsiMultiCondition1St: string }>(),
);

/** Acción de éxito al cargar recetas del profesional (versión 2) */
export const LOAD_RECIPES_BY_PROFESSIONAL_V2_OK  = createAction(
    '[medicamentos] OK medicamentos por paciente',
  props<{ recipes: ReportRecipeResponse[] }>(),
);

/** Acción de error al cargar recetas del profesional (versión 2) */
export const LOAD_RECIPES_BY_PROFESSIONAL_V2_ERR = createAction(
    '[medicamentos] ERR medicamentos por paciente',
    props<{ payload: HttpErrorResponse }>(),
);

/** Acción para eliminar una receta médica */
export const DELETE_RECIPE     = createAction(
    '[medicamentos] DELETE Receta Médica',
  props<{ recipe: Paperwork<Recipe | MagistralRecipe> }>(),
);

/** Acción de éxito al eliminar una receta médica */
export const DELETE_RECIPE_OK  = createAction(
    '[medicamentos] OK DELETE Receta Médica',
  props<{ recipe: Paperwork<Recipe | MagistralRecipe> }>(),
);

/** Acción de error al eliminar una receta médica */
export const DELETE_RECIPE_ERR = createAction(
    '[medicamentos] ERR DELETE Receta Médica',
    props<{ payload: HttpErrorResponse }>(),
);

/** Acción para resetear el estado después de completar una operación de receta */
export const PUT_RECIPE_END   = createAction('[medicamentos] Resetea el estado para crear una nueva solicitud');

/** Acción para limpiar todas las recetas del store */
export const CLEAR_RECIPES   = createAction('[medicamentos] Limpiar medicamentos por paciente');

/** Acción para seleccionar una receta específica (para edición o duplicación) */
export const SELECTED_RECIPE = createAction(
    '[medicamentos] SELECTED Receta Médica',
  props<{ selected: Paperwork<Recipe | MagistralRecipe>; action: string }>(),
);

/** Acción para deseleccionar la receta actual */
export const UNSELECT_RECIPE = createAction('[medicamentos] UNSELECT Receta Médica');

/** Acción para cargar recetas de un paciente específico (versión 1) */
export const LOAD_RECIPES_BY_PATIENT     = createAction(
    '[medicamentos] Cargar obtener medicamentos por paciente',
    props<{ params: ParamsToFindByPatient }>(),
);

/** Acción de éxito al cargar recetas del paciente (versión 1) */
export const LOAD_RECIPES_BY_PATIENT_OK  = createAction(
    '[medicamentos] OK obtener medicamentos por paciente',
  props<{ recipes: Paperwork<Recipe | MagistralRecipe>[] }>(),
);

/** Acción de error al cargar recetas del paciente (versión 1) */
export const LOAD_RECIPES_BY_PATIENT_ERR = createAction(
    '[medicamentos] ERR obtener medicamentos por paciente',
    props<{ payload: HttpErrorResponse }>(),
);

/** Acción para cargar recetas de un paciente con formato de reporte (versión 2) */
export const LOAD_RECIPES_BY_PATIENT_V2     = createAction(
    '[medicamentos] Cargar obtener medicamentos por paciente',
    props<{ params: ParamsToFindByPatient }>(),
);

/** Acción de éxito al cargar recetas del paciente (versión 2) */
export const LOAD_RECIPES_BY_PATIENT_V2_OK  = createAction(
    '[medicamentos] OK obtener medicamentos por paciente',
  props<{ recipes: ReportRecipeResponse[] }>(),
);

/** Acción de error al cargar recetas del paciente (versión 2) */
export const LOAD_RECIPES_BY_PATIENT_V2_ERR = createAction(
    '[medicamentos] ERR obtener medicamentos por paciente',
    props<{ payload: HttpErrorResponse }>(),
);
